import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  resolveAdventureStatus,
  resolveAdventureMissionType,
  resolveNarrativeTime,
  narrativeTimeLabel,
} from '../../features/adventure/shared/adventureConstants'
import './AdventureList.css'

const MISSION_SYMBOLS = {
  Combate: '⚔',
  Espionaje: '◈',
  Rescate: '◇',
  Exploración: '◉',
  Comercio: '◆',
  Diplomacia: '✦',
  Misión: '⚔',
  General: '◈',
}

const DIFFICULTY_CONFIG = {
  Social: { label: 'Social', cls: 'difficulty--social' },
  Principiante: { label: 'Principiante', cls: 'difficulty--beginner' },
  Intermedio: { label: 'Veterano', cls: 'difficulty--intermediate' },
  Avanzado: { label: 'Élite', cls: 'difficulty--advanced' },
  Extremo: { label: 'Legendario', cls: 'difficulty--extreme' },
}

const STATUS_CONFIG = {
  Abierta: { symbol: '●', cls: 'adv-status--open', label: 'Abierta' },
  'En progreso': { symbol: '◆', cls: 'adv-status--progress', label: 'En curso' },
  Completada: { symbol: '◇', cls: 'adv-status--completed', label: 'Cerrada' },
  Cancelada: { symbol: '✕', cls: 'adv-status--cancelled', label: 'Cancelada' },
}

const AdventureList = ({ adventures = [], layout = 'list', variant = 'compact', showTerritory = false }) => {
  const navigate = useNavigate()
  const getMissionIcon = (missionType) => MISSION_SYMBOLS[missionType] || '◈'

  const getDifficultyConfig = (difficulty) =>
    DIFFICULTY_CONFIG[difficulty] || DIFFICULTY_CONFIG['Principiante']

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return 'Hace un momento'

    const now = new Date()
    const time = new Date(timestamp)
    const diffInSeconds = Math.floor((now - time) / 1000)

    if (diffInSeconds < 60) return 'Hace un momento'
    if (diffInSeconds < 3600) return `Hace ${Math.floor(diffInSeconds / 60)} min`
    if (diffInSeconds < 86400) return `Hace ${Math.floor(diffInSeconds / 3600)} h`
    if (diffInSeconds < 2592000) return `Hace ${Math.floor(diffInSeconds / 86400)} días`
    return `Hace ${Math.floor(diffInSeconds / 2592000)} meses`
  }

  const capitalizeFirstLetter = (text) => {
    if (!text) return ''
    return text.charAt(0).toUpperCase() + text.slice(1)
  }

  const extractPreview = (content, previewText, maxLen = 350) => {
    let text = previewText || content || 'Sin descripción disponible…'

    if (!previewText && content) {
      text = content
        .replace(/<[^>]*>/g, '')
        .replace(/\[([^\]]*)\]/g, '')
        .trim()
    }

    if (Number.isFinite(maxLen) && maxLen > 0 && text.length > maxLen) {
      text = `${text.substring(0, maxLen).trim()}…`
    }

    return capitalizeFirstLetter(text)
  }

  if (!adventures || adventures.length === 0) {
    return (
      <div className="adventure-list-empty" role="status">
        <div className="adv-empty">
          <span className="adv-empty__seal" aria-hidden="true">
            記
          </span>
          <p className="adv-empty__eyebrow">Archivo vacío</p>
          <h3 className="adv-empty__title">No hay aventuras que mostrar</h3>
          <p className="adv-empty__text">
            No hay crónicas en este listado. Prueba a ajustar los filtros o vuelve más tarde cuando
            haya nuevas escenas abiertas.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`adventure-list${layout === 'grid' ? ' adventure-grid' : ''}`}
      role="list"
    >
      {adventures.map((adventure, index) => {
        const diffCfg = getDifficultyConfig(adventure.difficulty)
        const statusCfg = STATUS_CONFIG[resolveAdventureStatus(adventure)] || STATUS_CONFIG['Abierta']
        const participants = Number(adventure.current_participants || 0)
        const mission = resolveAdventureMissionType(adventure)
        const turnNum = Number(adventure.current_turn)
        const turnMax = Number(adventure.max_turns)
        const hasTurns = Number.isFinite(turnNum) && turnNum > 0
        const turnLabel = hasTurns
          ? Number.isFinite(turnMax) && turnMax > 0
            ? `Turno ${turnNum}/${turnMax}`
            : `Turno ${turnNum}`
          : null
        const adventureHref = adventure.slug ? `/aventura/${adventure.slug}` : null
        const openAdventure = () => {
          if (adventureHref) navigate(adventureHref)
        }
        const territoryName = adventure.territory_name || adventure.territory || null
        const territorySlug = adventure.territory_slug || adventure.territorySlug || null
        const isDangerous =
          String(adventure.studentAccess || adventure.student_access || 'friendly').toLowerCase() ===
          'dangerous'
        const timeKey = resolveNarrativeTime(adventure)
        const timeLabel = narrativeTimeLabel(timeKey)

        return (
          <article
            key={adventure.adventureId || adventure.slug || index}
            className={`adv-card adv-card--${variant}${adventureHref ? ' adv-card--clickable' : ''}${
              statusCfg.cls.includes('completed')
                ? ' adv-card--closed'
                : statusCfg.cls.includes('cancelled')
                  ? ' adv-card--cancelled'
                  : statusCfg.cls.includes('progress')
                    ? ' adv-card--in-progress'
                    : ''
            }`}
            role="listitem"
            tabIndex={adventureHref ? 0 : undefined}
            aria-label={
              adventureHref
                ? `Abrir aventura: ${capitalizeFirstLetter(adventure.title)}`
                : undefined
            }
            style={{ '--adv-i': index }}
            onClick={adventureHref ? openAdventure : undefined}
            onKeyDown={
              adventureHref
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      openAdventure()
                    }
                  }
                : undefined
            }
          >
            <aside className="adv-card__seal" aria-hidden="true">
              <span className="adv-card__seal-mark">{getMissionIcon(mission)}</span>
            </aside>

            <div className="adv-card__main">
              <header className="adv-card__kicker">
                <span className={`adv-card__status ${statusCfg.cls}`} title={`Estado: ${statusCfg.label}`}>
                  <span className="adv-card__status-mark" aria-hidden="true">
                    {statusCfg.symbol}
                  </span>
                  <span className="adv-card__status-label">{statusCfg.label}</span>
                </span>
                <span className="adv-card__mission-type">{mission.toUpperCase()}</span>
                <span className={`adv-card__difficulty ${diffCfg.cls}`}>{diffCfg.label}</span>
                <span
                  className={`adv-card__time adv-card__time--${timeKey}`}
                  title={
                    timeKey === 'presente'
                      ? 'Presente: prioridad de consecuencias duras'
                      : 'Pasado: sin prioridad de consecuencias duras'
                  }
                >
                  {timeLabel}
                </span>
                {isDangerous && (
                  <span className="adv-card__danger" title="No apta para estudiantes de academia">
                    Peligrosa
                  </span>
                )}
              </header>

              <h3 className="adv-card__title">{capitalizeFirstLetter(adventure.title)}</h3>

              <div className="adv-card__meta">
                <span>
                  Crónica de{' '}
                  {adventure.charSlug ? (
                    <Link
                      to={`/personajes/${adventure.charSlug}`}
                      className="adv-card__author"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {adventure.author || 'un viajero sin nombre'}
                    </Link>
                  ) : (
                    <span className="adv-card__author">
                      {adventure.author || 'un viajero sin nombre'}
                    </span>
                  )}
                  {showTerritory && territoryName && (
                    <>
                      {' '}
                      en{' '}
                      {territorySlug ? (
                        <Link
                          to={`/han/${territorySlug}`}
                          className="adv-card__han-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {territoryName}
                        </Link>
                      ) : (
                        <span className="adv-card__han">{territoryName}</span>
                      )}
                    </>
                  )}
                  ,{' '}
                  <time dateTime={adventure.last_activity || undefined}>
                    {getTimeAgo(adventure.last_activity).toLowerCase()}
                  </time>
                </span>
              </div>

              <p className="adv-card__preview">
                {extractPreview(adventure.content, adventure.preview_text)}
              </p>

              <footer className="adv-card__footer">
                <div className="adv-card__stats">
                  {turnLabel && (
                    <span className="adv-card__stat adv-card__stat--turn">{turnLabel}</span>
                  )}
                  <span className="adv-card__stat">
                    <strong>{participants}</strong>
                    {participants === 1 ? ' shinobi' : ' shinobis'}
                  </span>
                  <span className="adv-card__stat">
                    <strong>{adventure.replies_count || 0}</strong> resp.
                  </span>
                  <span className="adv-card__stat">
                    <strong>{adventure.views || 0}</strong> vistas
                  </span>
                </div>
              </footer>
            </div>

            <span className="adv-card__chevron" aria-hidden="true">
              →
            </span>
          </article>
        )
      })}
    </div>
  )
}

export default AdventureList
